import type { PublicPlanCatalogRow, SchoolSubscriptionSummaryRow } from './api/billing.client';

export type SubscriptionDisplayStatus = 'trial' | 'active' | 'past_due' | 'suspended' | 'other';

export function normalizeSubscriptionStatus(status: string | undefined | null): SubscriptionDisplayStatus {
  const s = (status || '').toLowerCase();
  if (s === 'trial') return 'trial';
  if (s === 'active') return 'active';
  if (s === 'past_due') return 'past_due';
  if (s === 'suspended') return 'suspended';
  return 'other';
}

export function subscriptionStatusLabel(status: string | undefined | null): string {
  const kind = normalizeSubscriptionStatus(status);
  switch (kind) {
    case 'trial':
      return 'Trial';
    case 'active':
      return 'Active';
    case 'past_due':
      return 'Past Due';
    case 'suspended':
      return 'Suspended';
    default:
      return status ? status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Unknown';
  }
}

export function formatSubscriptionBadgeText(summary: SchoolSubscriptionSummaryRow): string {
  const plan = summary.plan_name || summary.plan_key || 'Plan';
  const statusLabel = subscriptionStatusLabel(summary.status);
  if (normalizeSubscriptionStatus(summary.status) === 'trial') {
    const days =
      summary.trial_days_remaining != null && summary.trial_days_remaining >= 0
        ? summary.trial_days_remaining
        : null;
    return days != null ? `${plan} Trial • ${days} day${days === 1 ? '' : 's'} left` : `${plan} Trial`;
  }
  return `${plan} ${statusLabel}`;
}

export function canCompleteSubscriptionCheckout(
  summary: SchoolSubscriptionSummaryRow | null | undefined,
  catalog?: PublicPlanCatalogRow | null
): boolean {
  if (!summary) return false;
  const raw = (summary.status || '').toLowerCase();
  if (raw !== 'trial' && raw !== 'expired') return false;
  const pk = (summary.plan_key || '').toUpperCase();
  if (pk === 'ENTERPRISE' || summary.is_enterprise_custom) return false;
  if (catalog && !catalog.stripe_checkout_enabled) return false;
  if (catalog && !(catalog.available_cycles?.length ?? 0)) return false;
  return true;
}

/** True when school admin should see subscribe / restore-access checkout UI. */
export function shouldShowSubscriptionCheckout(
  summary: SchoolSubscriptionSummaryRow | null | undefined,
  catalog?: PublicPlanCatalogRow | null
): boolean {
  if (!summary) return false;
  const raw = (summary.status || '').toLowerCase();
  if (raw === 'trial') return true;
  if (raw === 'expired') return canCompleteSubscriptionCheckout(summary, catalog);
  return false;
}

export function checkoutBillingCycles(catalog?: PublicPlanCatalogRow | null): Array<'monthly' | 'yearly'> {
  const fromCatalog = (catalog?.available_cycles ?? []).filter(
    (c): c is 'monthly' | 'yearly' => c === 'monthly' || c === 'yearly'
  );
  return fromCatalog.length ? fromCatalog : ['monthly', 'yearly'];
}

export function isStripeCheckoutConfigured(catalog?: PublicPlanCatalogRow | null): boolean {
  if (!catalog?.stripe_checkout_enabled) return false;
  return (catalog.available_cycles?.length ?? 0) > 0;
}

export function formatTrialEndDate(summary: SchoolSubscriptionSummaryRow): string {
  const raw = summary.trial_expires_at || summary.end_date;
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleDateString(undefined, { dateStyle: 'medium', timeZone: 'UTC' });
  } catch {
    return raw.slice(0, 10);
  }
}

export function resolvePlanPricing(
  summary: SchoolSubscriptionSummaryRow,
  catalog: PublicPlanCatalogRow | undefined,
  billingCycle: 'monthly' | 'yearly'
): { amount: number | null; label: string } {
  if (summary.is_enterprise_custom) {
    const amount =
      billingCycle === 'yearly'
        ? summary.custom_yearly_price ?? null
        : summary.custom_monthly_price ?? null;
    if (amount != null) {
      return {
        amount,
        label: billingCycle === 'yearly' ? `$${amount.toLocaleString()}/yr (contract)` : `$${amount.toLocaleString()}/mo (contract)`,
      };
    }
  }
  const monthly = catalog?.monthly_price_usd ?? summary.monthly_price_usd;
  const annual = catalog?.annual_price_usd ?? summary.annual_price_usd;
  const amount = billingCycle === 'yearly' ? annual : monthly;
  if (amount != null && amount > 0) {
    return {
      amount,
      label: billingCycle === 'yearly' ? `$${amount.toLocaleString()}/yr` : `$${amount.toLocaleString()}/mo`,
    };
  }
  return { amount: null, label: '—' };
}

export function badgeToneClass(status: string | undefined | null): string {
  switch (normalizeSubscriptionStatus(status)) {
    case 'trial':
      return 'bg-amber-400/90 text-amber-950 ring-amber-300/50';
    case 'active':
      return 'bg-emerald-400/90 text-emerald-950 ring-emerald-300/50';
    case 'past_due':
      return 'bg-red-400/90 text-red-950 ring-red-300/50';
    case 'suspended':
      return 'bg-slate-300/90 text-slate-900 ring-slate-400/50';
    default:
      return 'bg-white/90 text-slate-800 ring-white/40';
  }
}
